// Exchange Rate service — API_INTEGRATION.md §3.33
// Manages currency exchange rates for multi-currency billing.
// Vertical: SHARED. No feature flag.

import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ExchangeRate {
  id: string;
  tenant_id: string;
  from_currency: string; // ISO 4217
  to_currency: string;
  rate: number;
  effective_date: string;
  created_at: string;
  updated_at: string;
}

export interface CreateExchangeRateDto {
  from_currency: string;
  to_currency: string;
  rate: string; // numeric string — backend DTO validates with @IsNumberString to preserve NUMERIC(18,8) precision
  effective_date?: string;
}

export interface UpdateExchangeRateDto {
  rate?: string; // numeric string — see CreateExchangeRateDto.rate
  effective_date?: string;
}

export interface ExchangeRateListParams {
  page?: number;
  limit?: number;
  from_currency?: string;
  to_currency?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const exchangeRateService = {
  async list(params: ExchangeRateListParams = {}): Promise<{
    data: ExchangeRate[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 20,
      from_currency: params.from_currency,
      to_currency: params.to_currency,
    });
    return apiClientPaginated<ExchangeRate[]>(`/exchange-rates${q}`);
  },

  async get(id: string): Promise<ExchangeRate> {
    return apiClient<ExchangeRate>(`/exchange-rates/${id}`);
  },

  async create(dto: CreateExchangeRateDto): Promise<ExchangeRate> {
    return apiClient<ExchangeRate>('/exchange-rates', { method: 'POST', body: dto });
  },

  async update(id: string, dto: UpdateExchangeRateDto): Promise<ExchangeRate> {
    return apiClient<ExchangeRate>(`/exchange-rates/${id}`, { method: 'PATCH', body: dto });
  },

  async delete(id: string): Promise<void> {
    await apiClient<void>(`/exchange-rates/${id}`, { method: 'DELETE' });
  },

  /**
   * The rate a purchase order starts with: the latest `from → to` on or before
   * `date`, else 1 / the reverse pair, else null (nothing recorded).
   */
  async lookup(from: string, to: string, date?: string): Promise<ExchangeRateLookup | null> {
    return apiClient<ExchangeRateLookup | null>(`/exchange-rates/lookup${buildQuery({ from, to, date })}`);
  },
};

export interface ExchangeRateLookup {
  /** Units of `to` per 1 `from`, 8 decimals. */
  rate: string;
  effective_date: string;
  /** Worked out from the reverse pair. */
  inverse: boolean;
}

export { ApiError };
